/**
 * Authentication — Amazon Cognito via OpenID Connect (authorization code + PKCE).
 *
 * Configured with VITE_COGNITO_* env vars. When they are absent (local
 * development) auth is disabled: the app runs in "local mode" and the
 * backend's development user handles protected endpoints.
 */

import { createContext, useContext, useEffect, useMemo, type ReactNode } from "react";
import { AuthProvider as OidcProvider, useAuth as useOidc } from "react-oidc-context";
import { WebStorageStateStore, type User } from "oidc-client-ts";
import { useQueryClient } from "@tanstack/react-query";
import { setTokenProvider } from "@/lib/api/client";

const region = import.meta.env.VITE_COGNITO_REGION || "us-east-1";
const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID;
const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
/** Hosted login domain, e.g. "auth.deenoverdunya.org" or "<prefix>.auth.us-east-1.amazoncognito.com". */
const loginDomain = import.meta.env.VITE_COGNITO_DOMAIN;

export const AUTH_ENABLED = Boolean(userPoolId && clientId && loginDomain);

const RETURN_TO_KEY = "dod:auth:returnTo";
export const CALLBACK_PATH = "/auth/callback";

export interface AuthUser {
  email?: string;
  name?: string;
  groups: string[];
}

export interface AuthState {
  /** False in local mode (no Cognito configured). */
  enabled: boolean;
  isLoading: boolean;
  isAuthenticated: boolean;
  /** Signed in, or local mode — i.e. protected API calls are expected to succeed. */
  canUseAccount: boolean;
  isAdmin: boolean;
  /** Scholar or admin: may use the review portal. */
  isScholar: boolean;
  user: AuthUser | null;
  error: Error | undefined;
  signIn: () => void;
  signOut: () => void;
}

const LOCAL_MODE: AuthState = {
  enabled: false,
  isLoading: false,
  isAuthenticated: false,
  canUseAccount: true,
  isAdmin: true,
  isScholar: true,
  user: null,
  error: undefined,
  signIn: () => undefined,
  signOut: () => undefined,
};

const AuthContext = createContext<AuthState>(LOCAL_MODE);

export function useAuth(): AuthState {
  return useContext(AuthContext);
}

function toAuthUser(user: User): AuthUser {
  const profile = user.profile as Record<string, unknown>;
  const groups = Array.isArray(profile["cognito:groups"]) ? (profile["cognito:groups"] as string[]) : [];
  return {
    email: typeof profile.email === "string" ? profile.email : undefined,
    name: typeof profile.name === "string" ? profile.name : undefined,
    groups,
  };
}

/** Bridges react-oidc-context into our AuthState and the API client's token. */
function CognitoBridge({ children }: { children: ReactNode }) {
  const oidc = useOidc();
  const queryClient = useQueryClient();
  const idToken = oidc.user?.id_token;

  // The API verifies the ID token (it carries the email and group claims).
  useEffect(() => {
    setTokenProvider(() => idToken);
    // Signed-in state changed: refetch anything user-specific.
    queryClient.invalidateQueries();
  }, [idToken, queryClient]);

  const state = useMemo<AuthState>(() => {
    const user = oidc.user && !oidc.user.expired ? toAuthUser(oidc.user) : null;
    return {
      enabled: true,
      isLoading: oidc.isLoading,
      isAuthenticated: !!user,
      canUseAccount: !!user,
      isAdmin: !!user?.groups.includes("admin"),
      isScholar: !!user?.groups.some((g) => g === "scholar" || g === "admin"),
      user,
      error: oidc.error,
      signIn: () => {
        sessionStorage.setItem(RETURN_TO_KEY, window.location.pathname + window.location.search);
        void oidc.signinRedirect();
      },
      signOut: () => {
        void oidc.removeUser();
        // Cognito's logout endpoint ends the hosted-login session too.
        const logoutUri = encodeURIComponent(window.location.origin);
        window.location.href = `https://${loginDomain}/logout?client_id=${clientId}&logout_uri=${logoutUri}`;
      },
    };
  }, [oidc]);

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  if (!AUTH_ENABLED) {
    return <AuthContext.Provider value={LOCAL_MODE}>{children}</AuthContext.Provider>;
  }
  return (
    <OidcProvider
      authority={`https://cognito-idp.${region}.amazonaws.com/${userPoolId}`}
      client_id={clientId}
      redirect_uri={`${window.location.origin}${CALLBACK_PATH}`}
      post_logout_redirect_uri={window.location.origin}
      response_type="code"
      scope="openid email profile"
      automaticSilentRenew
      userStore={new WebStorageStateStore({ store: window.localStorage })}
      onSigninCallback={() => {
        const returnTo = sessionStorage.getItem(RETURN_TO_KEY) || "/";
        sessionStorage.removeItem(RETURN_TO_KEY);
        window.history.replaceState({}, document.title, returnTo);
        window.dispatchEvent(new PopStateEvent("popstate"));
      }}
    >
      <CognitoBridge>{children}</CognitoBridge>
    </OidcProvider>
  );
}
